// src/pages/CommunityProfile.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUsers,
  FiMapPin,
  FiGlobe,
  FiCalendar,
  FiSettings,
  FiUserPlus,
  FiUserCheck,
  FiClock,
  FiInstagram,
  FiLinkedin,
  FiTwitter,
} from 'react-icons/fi';
import { useAuth } from '../services/auth';
import {
  getCommunityByUsername,
  getUserMembershipStatus,
  getCommunityAnnouncements,
  getCommunityEvents,
  getCommunityMembers,
  getUserAccessRequestStatus,
} from '../services/communityService';
import DownloadAppModal from '../components/DownloadAppModal';
import '../styles/CommunityProfile.scss';
import { parseLocalDate } from '../shared/eventDateTime';

const CommunityProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState(null);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [accessRequestStatus, setAccessRequestStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('events');
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [actionLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadModalFeature, setDownloadModalFeature] = useState('');

  // The signed-in person's id, not the user object: the object is replaced on
  // every auth refresh, which re-ran the community load on unrelated changes
  // (why it was once left out of the dependencies altogether). The id only
  // changes when someone signs in or out - exactly when this should reload.
  const userId = user?.id ?? null;

  const fetchCommunityData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch community
      const communityData = await getCommunityByUsername(username);
      setCommunity(communityData);

      if (userId) {
        setIsOwner(communityData.created_by === userId);

        // Fetch membership status
        const membership = await getUserMembershipStatus(communityData.id, userId);
        setMembershipStatus(membership);

        // Fetch access request status if not a member
        if (!membership) {
          const request = await getUserAccessRequestStatus(communityData.id, userId);
          setAccessRequestStatus(request);
        }

        // Fetch announcements preview
        const announcementsData = await getCommunityAnnouncements(communityData.id, 3);
        setAnnouncements(announcementsData);
      }
    } catch (error) {
      console.error('Error fetching community:', error);
    } finally {
      setLoading(false);
    }
  }, [username, userId]);

  useEffect(() => {
    if (!authLoading && username) {
      fetchCommunityData();
    }
  }, [username, authLoading, fetchCommunityData]); // Removed 'user' from dependencies to prevent refresh on every tab change

  const fetchTabData = useCallback(async () => {
    try {
      if (activeTab === 'events') {
        const eventsData = await getCommunityEvents(community.id, { status: 'published' });
        setEvents(eventsData);
      } else if (activeTab === 'members') {
        const membersData = await getCommunityMembers(community.id, { status: 'active' });
        setMembers(membersData);
      }
    } catch (error) {
      console.error('Error fetching tab data:', error);
    }
  }, [activeTab, community]);

  useEffect(() => {
    if (community && user) {
      fetchTabData();
    }
  }, [activeTab, community, user, fetchTabData]);

  const handleJoinCommunity = () => {
    setDownloadModalFeature('join communities');
    setShowDownloadModal(true);
  };

  const handleLeaveCommunity = () => {
    setDownloadModalFeature('leave communities');
    setShowDownloadModal(true);
  };

  const handleManageCommunity = () => {
    setDownloadModalFeature('manage your community');
    setShowDownloadModal(true);
  };

  const handleMemberClick = (username) => {
    // Navigate to member profile (existing functionality can stay)
    // Don't add @ symbol if it's already there
    const cleanUsername = username.startsWith('@') ? username.substring(1) : username;
    navigate(`/${cleanUsername}`);
  };

  const getJoinButtonContent = () => {
    if (isOwner) {
      return {
        icon: <FiSettings size={20} />,
        text: 'Manage',
        onClick: handleManageCommunity,
        className: 'manage-btn',
      };
    }

    if (membershipStatus?.status === 'active') {
      return {
        icon: <FiUserCheck size={20} />,
        text: 'Member',
        onClick: handleLeaveCommunity,
        className: 'member-btn',
      };
    }

    if (accessRequestStatus?.status === 'pending') {
      return {
        icon: <FiClock size={20} />,
        text: 'Pending',
        onClick: null,
        className: 'pending-btn',
        disabled: true,
      };
    }

    return {
      icon: <FiUserPlus size={20} />,
      text: 'Join',
      onClick: handleJoinCommunity,
      className: 'join-btn',
    };
  };

  const getImageSource = () => {
    return community?.cover_image_cdn_url || community?.cover_image_direct_url || '/images/communities/default.jpg';
  };

  const formatDate = (dateString) => {
    const date = (parseLocalDate(dateString) ?? new Date(NaN));
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading || authLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading community...</p>
      </div>
    );
  }

  if (!community) {
    return (
      <div className="loading-screen">
        <h2>Community not found</h2>
        <button onClick={() => navigate('/communities')} className="back-btn">
          Go to Communities
        </button>
      </div>
    );
  }

  const joinButton = getJoinButtonContent();

  return (
    <div className="community-profile-page">
      {/* Background */}
      <div className="community-background">
        <img src={getImageSource()} alt="" />
        <div className="background-overlay" />
      </div>

      {/* Header */}
      <motion.div
        className="community-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="community-info">
          <div className="community-avatar">
            <img src={getImageSource()} alt={community.name} />
          </div>

          <div className="community-details">
            <h1>{community.name}</h1>

            <div className="community-meta">
              {community.city && (
                <div className="meta-item">
                  <FiMapPin size={16} />
                  <span>{community.city}, {community.state}</span>
                </div>
              )}

              <div className="meta-item">
                <FiUsers size={16} />
                <span>{community.members_count} {community.members_count === 1 ? 'member' : 'members'}</span>
              </div>

              {community.events_count > 0 && (
                <div className="meta-item">
                  <FiCalendar size={16} />
                  <span>{community.events_count} {community.events_count === 1 ? 'event' : 'events'}</span>
                </div>
              )}
            </div>

            {community.description && (
              <p className="community-description">{community.description}</p>
            )}

            {/* Social Links */}
            {(community.instagram_url || community.linkedin_url || community.twitter_url || community.website_url) && (
              <div className="social-links">
                {community.instagram_url && (
                  <a href={community.instagram_url} target="_blank" rel="noopener noreferrer" className="social-link">
                    <FiInstagram size={20} />
                  </a>
                )}
                {community.twitter_url && (
                  <a href={community.twitter_url} target="_blank" rel="noopener noreferrer" className="social-link">
                    <FiTwitter size={20} />
                  </a>
                )}
                {community.linkedin_url && (
                  <a href={community.linkedin_url} target="_blank" rel="noopener noreferrer" className="social-link">
                    <FiLinkedin size={20} />
                  </a>
                )}
                {community.website_url && (
                  <a href={community.website_url} target="_blank" rel="noopener noreferrer" className="social-link">
                    <FiGlobe size={20} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="community-actions">
          <button
            className={`action-btn ${joinButton.className}`}
            onClick={joinButton.onClick}
            disabled={joinButton.disabled || actionLoading}
          >
            {joinButton.icon}
            {joinButton.text}
          </button>
        </div>
      </motion.div>

      {/* Announcements Preview */}
      {announcements.length > 0 && (
        <motion.div
          className="announcements-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="section-header">
            <h2>Announcements</h2>
          </div>

          <div className="announcements-scroll">
            {announcements.map((announcement) => (
              <div key={announcement.id} className="announcement-card">
                <div className="announcement-header">
                  <img
                    src={announcement.creator.profile_photo_cdn_url || '/images/default-avatar.png'}
                    alt={announcement.creator.first_name}
                    className="creator-avatar"
                  />
                  <div className="creator-info">
                    <span className="creator-name">
                      {announcement.creator.first_name} {announcement.creator.last_name}
                    </span>
                    <span className="announcement-date">{formatDate(announcement.created_at)}</span>
                  </div>
                </div>
                <p className="announcement-content">{announcement.content}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Tabs */}
      <motion.div
        className="community-tabs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <button
          className={`tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Events
        </button>
        <button
          className={`tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          Members
        </button>
      </motion.div>

      {/* Tab Content */}
      <div className="tab-content">
        <AnimatePresence mode="wait">
          {activeTab === 'events' && (
            <motion.div
              key="events"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="events-grid"
            >
              {events.length === 0 ? (
                <div className="empty-state">
                  <FiCalendar size={48} />
                  <p>No upcoming events</p>
                </div>
              ) : (
                events.map((event) => (
                  <div
                    key={event.id}
                    className="event-card"
                    onClick={() => navigate(`/event/${event.id}`)}
                  >
                    <div className="event-image-container">
                      <img
                        src={event.cover_image_cdn_url || event.cover_image_direct_url || '/images/events/default.jpg'}
                        alt={event.name}
                        className="event-image"
                      />
                      <div className="event-gradient" />
                    </div>

                    <div className="event-content">
                      <h3 className="event-name">{event.name}</h3>
                      <div className="event-detail">
                        <FiCalendar size={14} />
                        <span>{formatDate(event.event_date)}</span>
                      </div>
                      {event.location && (
                        <div className="event-detail">
                          <FiMapPin size={14} />
                          <span>{event.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </motion.div>
          )}

          {activeTab === 'members' && (
            <motion.div
              key="members"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="members-grid"
            >
              {members.length === 0 ? (
                <div className="empty-state">
                  <FiUsers size={48} />
                  <p>No members yet</p>
                </div>
              ) : (
                members.map((member) => (
                  <div
                    key={member.id}
                    className="member-card"
                    onClick={() => handleMemberClick(member.user.username)}
                  >
                    <img
                      src={member.user.profile_photo_cdn_url || '/images/default-avatar.png'}
                      alt={member.user.first_name}
                      className="member-avatar"
                    />
                    <div className="member-info">
                      <h4 className="member-name">
                        {member.user.first_name} {member.user.last_name}
                      </h4>
                      <p className="member-username">@{member.user.username}</p>
                      {member.role !== 'member' && (
                        <span className="member-role">{member.role}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Download App Modal */}
      <DownloadAppModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        feature={downloadModalFeature}
      />
    </div>
  );
};

export default CommunityProfile;
